/**
 * WhatsApp Integration Utility
 * Handles template processing and WhatsApp link generation
 */

/**
 * Process WhatsApp template by replacing placeholders with actual job data
 * @param {string} template - The WhatsApp message template with placeholders
 * @param {object} jobData - The job data object
 * @param {string} stdCode - The STD code from company settings (e.g., "+91")
 * @returns {string} - Processed message with placeholders replaced
 */
export const processWhatsAppTemplate = (template, jobData, stdCode = '+91', currencySymbol = '₹') => {
  if (!template || !jobData) {
    return getDefaultTemplate(jobData, stdCode);
  }

  let message = template;

  // Replace placeholders with actual data
  const replacements = {
    '<<CUST_NAME>>': jobData.customer?.name || 'Customer',
    '<<BRAND>>': jobData.deviceBrand || '',
    '<<MODEL>>': jobData.deviceModel || '',
    '<<JOB_ID>>': jobData.jobNumber || '',
    '<<DEVICE_CATEGORY>>': jobData.deviceCategory || 'Device',
    '<<STATUS>>': jobData.status?.replace('_', ' ') || '',
    '<<ESTIMATED_COST>>': jobData.estimatedCost ? `${currencySymbol}${jobData.estimatedCost.toFixed(2)}` : '',
    '<<ACTUAL_COST>>': jobData.actualCost ? `${currencySymbol}${jobData.actualCost.toFixed(2)}` : '',
    '<<BALANCE>>': calculateBalance(jobData, currencySymbol),
  };

  // Replace all placeholders
  Object.keys(replacements).forEach(placeholder => {
    message = message.replace(new RegExp(placeholder, 'g'), replacements[placeholder]);
  });

  return message;
};

/**
 * Calculate balance amount for a job
 * @param {object} jobData - The job data object
 * @param {string} currencySymbol - The active currency symbol
 * @returns {string} - Formatted balance amount
 */
const calculateBalance = (jobData, currencySymbol = '₹') => {
  const spareCost = jobData.jobItems?.reduce((sum, item) => sum + item.amount, 0) || 0;
  const totalCost = jobData.actualCost || (jobData.estimatedCost + spareCost);
  const paid = jobData.advanceAmount || 0;
  const balance = totalCost - paid;
  return balance > 0 ? `${currencySymbol}${balance.toFixed(2)}` : `${currencySymbol}0.00`;
};

/**
 * Get default WhatsApp template if branch template is not set
 * @param {object} jobData - The job data object
 * @param {string} stdCode - The STD code from company settings
 * @returns {string} - Default message template
 */
const getDefaultTemplate = (jobData, stdCode = '+91') => {
  const customerName = jobData.customer?.name || 'Customer';
  const deviceBrand = jobData.deviceBrand || '';
  const deviceModel = jobData.deviceModel || '';
  const jobNumber = jobData.jobNumber || '';
  const status = jobData.status?.replace('_', ' ') || 'In Progress';

  return `Dear ${customerName},

Your ${deviceBrand} ${deviceModel} (Job ID: ${jobNumber}) is ${status}.

Thank you for choosing our service!`;
};

/**
 * Generate WhatsApp web link with encoded message
 * @param {string} phoneNumber - Customer phone number (without country code)
 * @param {string} message - The message to send
 * @param {string} stdCode - The STD code from company settings (e.g., "+91")
 * @returns {string} - WhatsApp web URL
 */
export const generateWhatsAppLink = (phoneNumber, message, stdCode = '+91') => {
  if (!phoneNumber || !message) {
    return null;
  }

  // Clean phone number - remove spaces, dashes, and special characters
  let cleanPhone = phoneNumber.replace(/[\s\-\(\)+]/g, '');

  // If phone starts with country code, remove it
  if (cleanPhone.startsWith(stdCode.replace('+', ''))) {
    cleanPhone = cleanPhone.substring(stdCode.replace('+', '').length);
  }

  // Combine STD code with phone number
  const fullNumber = `${stdCode.replace('+', '')}${cleanPhone}`;

  // URL encode the message
  const encodedMessage = encodeURIComponent(message);

  // Generate WhatsApp web link
  return `https://wa.me/${fullNumber}?text=${encodedMessage}`;
};

/**
 * Open WhatsApp with pre-filled message
 * @param {object} jobData - The job data object
 * @param {string} template - The WhatsApp message template
 * @param {string} stdCode - The STD code from company settings
 */
export const sendWhatsAppMessage = (jobData, template, stdCode = '+91', currencySymbol = '₹') => {
  if (!jobData || !jobData.customer?.phone) {
    console.error('❌ WhatsApp Error: Missing job data or customer phone');
    return false;
  }

  try {
    // Process template
    const message = processWhatsAppTemplate(template, jobData, stdCode, currencySymbol);
    
    // Generate WhatsApp link
    const whatsappLink = generateWhatsAppLink(jobData.customer.phone, message, stdCode);
    
    if (!whatsappLink) {
      console.error('❌ WhatsApp Error: Failed to generate link');
      return false;
    }

    // Open WhatsApp in new window
    window.open(whatsappLink, '_blank', 'noopener,noreferrer');
    return true;
  } catch (error) {
    console.error('❌ WhatsApp Error:', error);
    return false;
  }
};

/**
 * Get available template placeholders
 * @returns {array} - List of available placeholders with descriptions
 */
export const getAvailablePlaceholders = () => {
  return [
    { placeholder: '<<CUST_NAME>>', description: 'Customer Name' },
    { placeholder: '<<BRAND>>', description: 'Device Brand' },
    { placeholder: '<<MODEL>>', description: 'Device Model' },
    { placeholder: '<<JOB_ID>>', description: 'Job Number' },
    { placeholder: '<<DEVICE_CATEGORY>>', description: 'Device Category (Mobile, Laptop, etc.)' },
    { placeholder: '<<STATUS>>', description: 'Current Job Status' },
    { placeholder: '<<ESTIMATED_COST>>', description: 'Estimated Cost' },
    { placeholder: '<<ACTUAL_COST>>', description: 'Actual Cost' },
    { placeholder: '<<BALANCE>>', description: 'Balance Amount Due' },
  ];
};
